import React, { useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { PublicHeader } from '@/components/public/PublicHeader'
import { PublicAuthModal } from '@/components/public/PublicAuthModal'
import { Loader2, ServerCrash } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { Helmet } from 'react-helmet-async'
import { canonicalizePath } from '@/lib/seo'

interface PublicPageData {
	id: string
	name: string
	slug: string
	description?: string | null
	logo_url?: string | null
	team: {
		id: string
		name: string
		logo_url: string | null
		slug: string
	}
}

export function PublicPortalLayout() {
	const { teamSlug: paramTeamSlug } = useParams<{ teamSlug: string }>()
	const { teamSlug: contextTeamSlug, isPortal } = useTeamSlug()
	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
	const location = useLocation()

	// Use context teamSlug for subdomains, fallback to URL param for backward compatibility
	const teamSlug = contextTeamSlug || paramTeamSlug

	const { data, isLoading, error } = useQuery<PublicPageData, Error>({
		queryKey: ['publicPageData', teamSlug, isPortal],
		queryFn: async () => {
			if (!teamSlug) throw new Error('Team slug is missing.')

			// Use different API endpoints based on whether we're on a subdomain
			if (isPortal && contextTeamSlug) {
				// Subdomain route - use the portal API that gets roadmap from subdomain context
				return apiClient<PublicPageData>('/api/portal/roadmap', {
					needsAuth: false,
				})
			} else {
				// Path-based route - use the public roadmap API
				return apiClient<PublicPageData>(`/api/public/roadmaps/${teamSlug}`, {
					needsAuth: false,
				})
			}
		},
		enabled: !!teamSlug,
		retry: 1,
	})

	if (isLoading) {
		// <<< MODIFIED: A better full-page loading state >>>
		return (
			<div className="flex min-h-screen w-full items-center justify-center bg-slate-50">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (error) {
		// <<< MODIFIED: A more user-friendly full-page error state >>>
		return (
			<div className="flex min-h-screen w-full flex-col items-center justify-center bg-slate-50 p-4">
				<ServerCrash className="h-12 w-12 text-destructive mb-4" />
				<h2 className="text-xl font-semibold mb-2">Workspace Not Found</h2>
				<p className="text-muted-foreground text-center mb-6">
					The workspace at this address could not be loaded. It may have been
					moved or deleted.
				</p>
				<Button asChild>
					<a href="/">Return to Main Site</a>
				</Button>
			</div>
		)
	}

	const roadmapForHeader = data
		? {
				name: data.name,
				slug: data.slug,
				logo_url: data.logo_url,
		  }
		: null

	const teamForHeader = data
		? {
				name: data.team.name,
				slug: data.team.slug,
				logo_url: data.team.logo_url,
		  }
		: null

	const canonicalUrl =
		typeof window !== 'undefined'
			? `${window.location.origin}${canonicalizePath(location.pathname)}`
			: ''

	const seoTitle = data
		? `${data.team.name}${data.name ? ` · ${data.name}` : ''} | Feedbask`
		: 'Public Portal | Feedbask'

	const seoDescription =
		data?.description ||
		`Public roadmap and updates for ${data?.team?.name || 'this workspace'}.`

	return (
		<div className="bg-background w-full min-h-screen">
			<Helmet>
				<title>{seoTitle}</title>
				<meta name="description" content={seoDescription} />
				{canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
				{canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
				<meta name="robots" content="noindex,follow" />
				<meta property="og:title" content={seoTitle} />
				<meta property="og:description" content={seoDescription} />
				<meta property="og:type" content="website" />
			</Helmet>
			<PublicHeader
				roadmap={roadmapForHeader}
				team={teamForHeader}
				onSignInClick={() => setIsAuthModalOpen(true)}
			/>
			<main className="w-full">
				<Outlet />
			</main>

			<PublicAuthModal
				isOpen={isAuthModalOpen}
				onClose={() => setIsAuthModalOpen(false)}
			/>
		</div>
	)
}
